import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useUser, SignInButton } from "../hooks/useAuthCompat";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useApi } from "../hooks/useApi";

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const api = useApi();
  const qc = useQueryClient();
  const { isSignedIn, isLoaded } = useUser();
  const [state, setState] = useState("polling");
  const attempts = useRef(0);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts.current >= 10) {
        if (!cancelled) setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const res = await api.get(`/checkout/session/${sessionId}`);
        if (res.data.status === "fulfilled") {
          setState("fulfilled");
          qc.invalidateQueries({ queryKey: ["library-home"] });
          qc.invalidateQueries({ queryKey: ["cart"] });
          return;
        }
      } catch {
        setState("error");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, isLoaded, isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isLoaded) {
    return (
      <div className="max-w-lg mx-auto px-6 py-28 text-center" data-testid="checkout-return-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-white/60" />
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="max-w-lg mx-auto px-6 py-28 text-center" data-testid="checkout-return-signin-required">
        <h1 className="font-display text-display-xs mb-4">Sign in to see your purchase</h1>
        <SignInButton>
          <button className="bg-white text-black px-6 py-3 text-sm uppercase tracking-wide hover:bg-white/80 transition-colors duration-200">Sign In</button>
        </SignInButton>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-6 py-28 text-center" data-testid="checkout-return-page">
      {state === "polling" && (
        <>
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-brandAccent" />
          <p className="text-white/70" data-testid="checkout-return-polling">Confirming your payment with Stripe…</p>
        </>
      )}
      {state === "fulfilled" && (
        <>
          <CheckCircle2 className="w-10 h-10 mx-auto mb-4 text-foreground" />
          <h1 className="font-display text-display-xs mb-3" data-testid="checkout-return-fulfilled">Purchase Confirmed</h1>
          <p className="text-white/70 mb-8">Your books are ready in My Library.</p>
          <Link to="/library" data-testid="checkout-return-library-link" className="text-xs uppercase tracking-wide text-brandAccent underline">Go to My Library</Link>
        </>
      )}
      {(state === "error" || state === "timeout") && (
        <>
          <XCircle className="w-10 h-10 mx-auto mb-4 text-foreground/75" />
          <h1 className="font-display text-display-xs mb-3" data-testid="checkout-return-error">We couldn't confirm this payment yet</h1>
          <p className="text-white/70 mb-8">If you were charged, your books will appear shortly once Stripe notifies us. Check your library in a minute.</p>
          <Link to="/library" data-testid="checkout-return-error-link" className="text-xs uppercase tracking-wide text-brandAccent underline">Go to My Library</Link>
        </>
      )}
    </div>
  );
}
